// MMG-4461: remember how the last page ended so the next launch can say whether WE reloaded it (update, login
// redirect), the user left it, or the browser ended it. Writes are synchronous localStorage so they land before a
// navigation; a browser kill writes nothing, which is itself the answer. Every call swallows its own errors.

export const PAGE_EXIT_KEY = 'med-gas-mobile_page-exit';
export const PAGE_HIDDEN_KEY = 'med-gas-mobile_page-hidden';

export type ForcedExitReason =
    | 'app-update-reload'
    | 'login-redirect'
    | 'logout-redirect'
    | 'job-validation-redirect'
    | 'error-screen-go-to-jobs'
    | 'service-worker-missing-reload';

export interface IPageExit {
    reason: ForcedExitReason | 'page-hidden';
    at: string;
    detail?: string | undefined;
}

export interface ILastPageExit {
    forced?: IPageExit | undefined; // one of our own reloads/redirects, written right before it happened
    hiddenAt?: string | undefined; // last time the page went to the background
    pagehideAt?: string | undefined; // the browser's own "page is going away" event (user left, tab closed, our reload)
}

const read = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};
const write = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // storage blocked (private mode / quota) - nothing to do
    }
};
const remove = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // ignore
    }
};

// Call immediately before any reload or full-page redirect the app itself performs
export const markForcedExit = (reason: ForcedExitReason, detail?: string): void => {
    const record: IPageExit = { reason, at: new Date().toISOString() };
    if (detail) record.detail = detail.slice(0, 200);
    write(PAGE_EXIT_KEY, JSON.stringify(record));
};

let captured: ILastPageExit | null = null;

// Read and clear what the previous page left behind. Runs once, as early as possible on startup.
export const capturePreviousPageExit = (): ILastPageExit => {
    if (captured) return captured;
    const result: ILastPageExit = {};
    try {
        const raw = read(PAGE_EXIT_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as IPageExit;
            if (parsed && parsed.reason === 'page-hidden') result.pagehideAt = parsed.at;
            else if (parsed && parsed.reason) result.forced = parsed;
        }
        const hidden = read(PAGE_HIDDEN_KEY);
        if (hidden) result.hiddenAt = hidden;
    } catch {
        // unreadable - treat as nothing recorded
    }
    remove(PAGE_EXIT_KEY);
    remove(PAGE_HIDDEN_KEY);
    captured = result;
    return result;
};

export const getPreviousPageExit = (): ILastPageExit | null => captured;

// One-line reading for the report
export const describePageExit = (exit: ILastPageExit | null): string => {
    if (!exit) return 'unknown';
    if (exit.forced) return `our ${exit.forced.reason}${exit.forced.detail ? ` (${exit.forced.detail})` : ''} at ${exit.forced.at}`;
    if (exit.pagehideAt) return `user left or closed the page at ${exit.pagehideAt}`;
    if (exit.hiddenAt) return `backgrounded at ${exit.hiddenAt}, then ended with no exit recorded (browser or OS ended it)`;
    return 'no exit recorded (browser or OS ended the page in the foreground)';
};

// Hook the browser's own signals. pagehide fires for navigation/close (and for our reloads, after the forced mark).
export const installPageExitTracking = (): void => {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.addEventListener('pagehide', () => {
            if (!read(PAGE_EXIT_KEY)) {
                const record: IPageExit = { reason: 'page-hidden', at: new Date().toISOString() };
                write(PAGE_EXIT_KEY, JSON.stringify(record));
            }
        });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') write(PAGE_HIDDEN_KEY, new Date().toISOString());
            else remove(PAGE_HIDDEN_KEY);
        });
    } catch {
        // never let tracking affect the app
    }
};

// Test hook
export const resetPageExitForTests = () => {
    captured = null;
};
